import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Copy,
  MessageSquare,
  Zap,
  ChevronDown,
  Save,
  AlertCircle,
  Lock,
  Users,
  Mail,
  RefreshCw,
  Eye,
  X,
  Info,
  Upload,
  FileCode,
  FileText,
} from 'lucide-react';
import { Badge, Spinner, useToast } from '@centraoneplus/ui';
import { ThemedSelect } from '@components/common/ThemedSelect';

/** Extract a user-friendly message from an API error without breaking on unknown shapes. */
function readApiError(err: any, fallback: string): string {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  );
}
import cannedResponsesService, {
  CannedResponse,
  CreateCannedResponseDto,
  extractVariables,
  replaceVariables,
  EMAIL_TEMPLATE_VARIABLES,
} from '@/services/canned-responses.service';

interface ResponseFormData {
  name: string;
  shortcut: string;
  content: string;
  category: string;
  isPrivate: boolean;
  isEmailTemplate: boolean;
  emailSubject: string;
  htmlTemplateFileId?: string;
  htmlTemplateFileName?: string;
  useUploadedTemplate: boolean;
}

const INITIAL_FORM: ResponseFormData = {
  name: '',
  shortcut: '',
  content: '',
  category: 'General',
  isPrivate: false,
  isEmailTemplate: false,
  emailSubject: '',
  htmlTemplateFileId: undefined,
  htmlTemplateFileName: undefined,
  useUploadedTemplate: false,
};

const DEFAULT_CATEGORIES = [
  { id: 'General', name: 'General', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  { id: 'Greetings', name: 'Greetings', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  { id: 'Closings', name: 'Closings', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  { id: 'Email Templates', name: 'Email Templates', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  { id: 'Technical', name: 'Technical', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' },
  { id: 'Escalation', name: 'Escalation', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
];

// Sample data for preview
const SAMPLE_PREVIEW_DATA: Record<string, string> = {
  'entity.contactName': 'John Smith',
  'entity.contactEmail': 'john.smith@example.com',
  'entity.ticketNumber': 'TKT-001234',
  'entity.subject': 'Quote Request - 10 Sites',
  'entity.organizationName': 'Voltx Power',
  'entity.totalFailed': '3',
  'entity.failedItemsText': `MPAN                  Reason
─────────────────────────────────────────
1900000163010         Invalid contract start date
1900008200370         Missing EAC value
1900009203558         Rate not available`,
  'entity.failedItemsHtml': '<table><tr><td>1900000163010</td><td>Invalid contract start date</td></tr></table>',
  // Single-brace variable format support
  'BrokerName': 'John Smith',
  'EACSection': `<div style="background:#fff3cd; border-left:4px solid #ffc107; padding:15px; margin:15px 0; border-radius:4px;">
    <strong>Missing EAC Values</strong>
    <p>The following MPANs are missing EAC values:</p>
    <ul><li>1900000163010</li><li>1900008200370</li></ul>
  </div>`,
  'LLFSection': `<div style="background:#f8d7da; border-left:4px solid #dc3545; padding:15px; margin:15px 0; border-radius:4px;">
    <strong>LLF Class Issues</strong>
    <p>Unable to determine LLF class for the following MPANs:</p>
    <ul><li>1900009203558</li></ul>
  </div>`,
  'StartDateSection': `<div style="background:#d1ecf1; border-left:4px solid #17a2b8; padding:15px; margin:15px 0; border-radius:4px;">
    <strong>Invalid Start Dates</strong>
    <p>The following MPANs have invalid contract start dates:</p>
    <ul><li>1900000163010 - Start date is in the past</li></ul>
  </div>`,
};

interface ResponseFormProps {
  initialData?: ResponseFormData;
  onSubmit: (data: ResponseFormData) => void;
  onCancel: () => void;
  isEdit?: boolean;
  isSaving?: boolean;
}

function ResponseForm({
  initialData = INITIAL_FORM,
  onSubmit,
  onCancel,
  isEdit = false,
  isSaving = false,
}: ResponseFormProps) {
  const [formData, setFormData] = useState<ResponseFormData>(initialData);
  const [errors, setErrors] = useState<Partial<ResponseFormData>>({});
  const [showPreview, setShowPreview] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const detectedVariables = extractVariables(formData.content + ' ' + formData.emailSubject);

  const handleFileUpload = useCallback(async (file: File) => {
    if (!file.name.endsWith('.html') && !file.name.endsWith('.htm')) {
      setUploadError('Please upload an HTML file (.html or .htm)');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setUploadError('File size must be less than 2MB');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const result = await cannedResponsesService.uploadHtmlTemplate(file);
      setFormData(prev => ({
        ...prev,
        htmlTemplateFileId: result.fileId,
        htmlTemplateFileName: result.fileName,
        content: result.content,
        useUploadedTemplate: true,
      }));
    } catch (err: any) {
      setUploadError(err.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  }, [handleFileUpload]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  }, [handleFileUpload]);

  const removeUploadedTemplate = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      htmlTemplateFileId: undefined,
      htmlTemplateFileName: undefined,
      useUploadedTemplate: false,
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const validate = (): boolean => {
    const newErrors: Partial<ResponseFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.shortcut && !formData.shortcut.startsWith('/') && !formData.shortcut.startsWith('#')) {
      newErrors.shortcut = 'Shortcut must start with / or #';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }

    if (formData.isEmailTemplate && !formData.emailSubject.trim()) {
      newErrors.emailSubject = 'Email subject is required for email templates';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const insertVariable = (variable: string) => {
    const template = `{{${variable}}}`;
    setFormData({ ...formData, content: formData.content + template });
  };

  const previewContent = replaceVariables(formData.content, SAMPLE_PREVIEW_DATA);
  const previewSubject = replaceVariables(formData.emailSubject, SAMPLE_PREVIEW_DATA);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Email Template Toggle */}
      <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
        <div className="flex items-center gap-3">
          <Mail className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          <div>
            <p className="text-sm font-medium text-purple-900 dark:text-purple-200">Use as Email Template</p>
            <p className="text-xs text-purple-700 dark:text-purple-400">Enable to use in workflow automation email actions</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setFormData({
            ...formData,
            isEmailTemplate: !formData.isEmailTemplate,
            category: !formData.isEmailTemplate ? 'Email Templates' : formData.category
          })}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
            formData.isEmailTemplate ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            formData.isEmailTemplate ? 'translate-x-5' : 'translate-x-0'
          }`} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Quote Validation Failed"
            className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
          />
          {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
        </div>

        {/* Shortcut */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Shortcut <span className="text-gray-400 dark:text-gray-500">(optional)</span>
          </label>
          <input
            type="text"
            value={formData.shortcut}
            onChange={(e) => setFormData({ ...formData, shortcut: e.target.value })}
            placeholder="/quote-failed"
            className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.shortcut ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
          />
          {errors.shortcut && <p className="text-xs text-red-500 mt-1">{errors.shortcut}</p>}
        </div>
      </div>

      {/* Email Subject (only for email templates) */}
      {formData.isEmailTemplate && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email Subject <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.emailSubject}
            onChange={(e) => setFormData({ ...formData, emailSubject: e.target.value })}
            placeholder="Unable to Generate Quote - {{entity.ticketNumber}}"
            className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.emailSubject ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
          />
          {errors.emailSubject && <p className="text-xs text-red-500 mt-1">{errors.emailSubject}</p>}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Category
          </label>
          <ThemedSelect
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {DEFAULT_CATEGORIES.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </ThemedSelect>
        </div>

        {/* Visibility */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Visibility
          </label>
          <div className="flex items-center gap-4 h-10">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={!formData.isPrivate}
                onChange={() => setFormData({ ...formData, isPrivate: false })}
                className="text-primary-600"
              />
              <Users className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Team</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={formData.isPrivate}
                onChange={() => setFormData({ ...formData, isPrivate: true })}
                className="text-primary-600"
              />
              <Lock className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Private</span>
            </label>
          </div>
        </div>
      </div>

      {/* HTML Template Upload (for email templates) */}
      {formData.isEmailTemplate && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FileCode className="h-5 w-5 text-green-600 dark:text-green-400" />
              <div>
                <p className="text-sm font-medium text-green-900 dark:text-green-200">Upload HTML Template</p>
                <p className="text-xs text-green-700 dark:text-green-400">Upload a custom HTML file for the acknowledgement email</p>
              </div>
            </div>
            {formData.useUploadedTemplate && (
              <Badge variant="success" className="text-xs">
                Using Uploaded Template
              </Badge>
            )}
          </div>

          {formData.htmlTemplateFileName ? (
            <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-green-300 dark:border-green-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded">
                  <FileText className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{formData.htmlTemplateFileName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">HTML template uploaded</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-1.5 text-xs text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 rounded hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
                >
                  Replace
                </button>
                <button
                  type="button"
                  onClick={removeUploadedTemplate}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors dark:text-gray-500"
                  title="Remove template"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                isDragging
                  ? 'border-green-500 bg-green-100 dark:bg-green-900/30'
                  : 'border-green-300 dark:border-green-700 hover:border-green-400 dark:hover:border-green-600'
              }`}
            >
              {isUploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Spinner size="md" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">Uploading template...</p>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-green-500 dark:text-green-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Drag and drop your HTML template here, or{' '}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-green-600 dark:text-green-400 hover:underline font-medium"
                    >
                      browse files
                    </button>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Supports .html and .htm files up to 2MB</p>
                </>
              )}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".html,.htm"
            onChange={handleFileInputChange}
            className="hidden"
          />

          {uploadError && (
            <div className="mt-2 flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertCircle className="h-4 w-4" />
              <p className="text-xs">{uploadError}</p>
            </div>
          )}

          <p className="mt-3 text-xs text-green-700 dark:text-green-400">
            <strong>Tip:</strong> Use variables like <code className="bg-green-100 dark:bg-green-800/30 px-1 rounded">{`{{entity.contactName}}`}</code> in your HTML template for dynamic content.
          </p>
        </div>
      )}

      {/* Available Variables (for email templates) */}
      {formData.isEmailTemplate && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 mb-2">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-blue-900 dark:text-blue-200">Available Variables</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {EMAIL_TEMPLATE_VARIABLES.map((v) => (
              <button
                key={v.key}
                type="button"
                onClick={() => insertVariable(v.key)}
                className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                title={v.description}
              >
                {`{{${v.key}}}`}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {formData.useUploadedTemplate ? 'HTML Template Content' : 'Content'} <span className="text-red-500">*</span>
            </label>
            {formData.useUploadedTemplate && (
              <Badge variant="success" className="text-xs">
                From uploaded file
              </Badge>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
          >
            <Eye className="h-3 w-3" />
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>
        {formData.useUploadedTemplate && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Content is populated from the uploaded HTML file. You can edit it below if needed.
          </p>
        )}
        <textarea
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          placeholder={formData.isEmailTemplate
            ? "Hi {{entity.contactName}},\n\nThank you for your request. We were unable to generate your quote for the following reasons:\n\n{{entity.failedItemsText}}\n\nPlease provide valid information so we can proceed.\n\nKind regards,\n{{entity.organizationName}}"
            : "Type your response here. Use {{variable_name}} for dynamic content."
          }
          rows={8}
          className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none font-mono ${errors.content ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
        />
        {errors.content && <p className="text-xs text-red-500 mt-1">{errors.content}</p>}

        {/* Detected Variables */}
        {detectedVariables.length > 0 && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 dark:text-gray-400">Detected variables:</span>
            {detectedVariables.map((v) => (
              <Badge key={v} variant="default" className="text-xs">
                {`{{${v}}}`}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Preview */}
      {showPreview && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Preview (with sample data)</span>
          </div>
          <div className="p-4 bg-white dark:bg-gray-900">
            {formData.isEmailTemplate && formData.emailSubject && (
              <div className="mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                <span className="text-xs text-gray-500 dark:text-gray-400">Subject: </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{previewSubject}</span>
              </div>
            )}
            {previewContent.trim().toLowerCase().startsWith('<!doctype') || previewContent.trim().toLowerCase().startsWith('<html') ? (
              <iframe
                srcDoc={previewContent}
                title="HTML Preview"
                className="w-full border-0 rounded"
                style={{ minHeight: '400px' }}
                sandbox="allow-same-origin"
              />
            ) : (
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{previewContent}</p>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-50"
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Spinner size="sm" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              {isEdit ? 'Update' : 'Create'} Response
            </>
          )}
        </button>
      </div>
    </form>
  );
}

interface ResponseCardProps {
  response: CannedResponse;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

function ResponseCard({ response, onEdit, onDelete, onDuplicate }: ResponseCardProps) {
  const [showContent, setShowContent] = useState(false);
  const category = DEFAULT_CATEGORIES.find(c => c.id === response.category) || DEFAULT_CATEGORIES[0];

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-colors bg-white dark:bg-gray-800">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-gray-900 dark:text-white">{response.name}</h4>
            {response.isEmailTemplate && (
              <Badge variant="info" className="text-xs flex items-center gap-1">
                <Mail className="h-3 w-3" />
                Email Template
              </Badge>
            )}
            {response.useUploadedTemplate && response.htmlTemplateFileName && (
              <Badge variant="success" className="text-xs flex items-center gap-1">
                <FileCode className="h-3 w-3" />
                HTML Template
              </Badge>
            )}
            {response.isPrivate && (
              <Lock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
            )}
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {response.shortcut && (
              <code className="text-sm text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 px-1.5 py-0.5 rounded">
                {response.shortcut}
              </code>
            )}
            <Badge variant="default" className={`text-xs ${category.color}`}>
              {category.name}
            </Badge>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Used {response.usageCount} times
            </span>
          </div>
          {response.isEmailTemplate && response.emailSubject && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
              Subject: {response.emailSubject}
            </p>
          )}
          {response.useUploadedTemplate && response.htmlTemplateFileName && (
            <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
              <FileText className="h-3 w-3" />
              Template: {response.htmlTemplateFileName}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowContent(!showContent)}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded dark:text-gray-500"
            title="Preview"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${showContent ? 'rotate-180' : ''}`} />
          </button>
          <button
            onClick={onDuplicate}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded dark:text-gray-500"
            title="Duplicate"
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            onClick={onEdit}
            className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded dark:text-gray-500"
            title="Edit"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded dark:text-gray-500"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {showContent && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono text-xs bg-gray-50 dark:bg-gray-900 p-3 rounded">{response.content}</p>
          {response.variables && response.variables.length > 0 && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 dark:text-gray-400">Variables:</span>
              {response.variables.map((v) => (
                <Badge key={v} variant="info" className="text-xs">
                  {`{{${v}}}`}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function CannedResponsesManager() {
  const toast = useToast();
  const [responses, setResponses] = useState<CannedResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showEmailTemplatesOnly, setShowEmailTemplatesOnly] = useState(false);

  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const loadResponses = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await cannedResponsesService.getAll({
        isEmailTemplate: showEmailTemplatesOnly ? true : undefined,
      });
      setResponses(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load canned responses');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadResponses();
  }, [showEmailTemplatesOnly]);

  const filteredResponses = responses.filter((r) => {
    const matchesSearch =
      !searchQuery ||
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.shortcut?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || r.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const editingResponse = editingId ? responses.find((r) => r.id === editingId) : null;

  const handleCreate = async (data: ResponseFormData) => {
    setIsSaving(true);
    try {
      const dto: CreateCannedResponseDto = {
        name: data.name,
        shortcut: data.shortcut || undefined,
        content: data.content,
        category: data.category,
        isPrivate: data.isPrivate,
        isEmailTemplate: data.isEmailTemplate,
        emailSubject: data.isEmailTemplate ? data.emailSubject : undefined,
        variables: extractVariables(data.content + ' ' + data.emailSubject),
        htmlTemplateFileId: data.htmlTemplateFileId,
        htmlTemplateFileName: data.htmlTemplateFileName,
        useUploadedTemplate: data.useUploadedTemplate,
      };
      await cannedResponsesService.create(dto);
      await loadResponses();
      setIsCreating(false);
      toast.success('Response created', `"${data.name}" has been created.`);
    } catch (err: any) {
      setError(err.message || 'Failed to create response');
      toast.error('Create failed', readApiError(err, 'Could not create the canned response.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async (data: ResponseFormData) => {
    if (!editingId) return;
    setIsSaving(true);
    try {
      await cannedResponsesService.update(editingId, {
        name: data.name,
        shortcut: data.shortcut || undefined,
        content: data.content,
        category: data.category,
        isPrivate: data.isPrivate,
        isEmailTemplate: data.isEmailTemplate,
        emailSubject: data.isEmailTemplate ? data.emailSubject : undefined,
        variables: extractVariables(data.content + ' ' + data.emailSubject),
        htmlTemplateFileId: data.htmlTemplateFileId,
        htmlTemplateFileName: data.htmlTemplateFileName,
        useUploadedTemplate: data.useUploadedTemplate,
      });
      await loadResponses();
      setEditingId(null);
      toast.success('Response updated', `"${data.name}" has been saved.`);
    } catch (err: any) {
      setError(err.message || 'Failed to update response');
      toast.error('Save failed', readApiError(err, 'Could not save the canned response.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicate = async (response: CannedResponse) => {
    try {
      await cannedResponsesService.create({
        name: `${response.name} (Copy)`,
        shortcut: response.shortcut ? `${response.shortcut}-copy` : undefined,
        content: response.content,
        category: response.category,
        isPrivate: response.isPrivate,
        isEmailTemplate: response.isEmailTemplate,
        emailSubject: response.emailSubject,
        variables: response.variables,
        htmlTemplateFileId: response.htmlTemplateFileId,
        htmlTemplateFileName: response.htmlTemplateFileName,
        useUploadedTemplate: response.useUploadedTemplate,
      });
      await loadResponses();
      toast.success('Response duplicated', `A copy of "${response.name}" has been created.`);
    } catch (err: any) {
      setError(err.message || 'Failed to duplicate response');
      toast.error('Duplicate failed', readApiError(err, 'Could not duplicate the canned response.'));
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await cannedResponsesService.delete(id);
      await loadResponses();
      setDeleteConfirm(null);
      toast.success('Response deleted', 'The canned response has been removed.');
    } catch (err: any) {
      setError(err.message || 'Failed to delete response');
      toast.error('Delete failed', readApiError(err, 'Could not delete the canned response.'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Canned Responses</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Create reusable message templates and email templates for workflow automation
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <button
            onClick={loadResponses}
            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg dark:text-gray-400"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setIsCreating(true)}
            className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 whitespace-nowrap"
            disabled={isCreating || editingId !== null}
          >
            <Plus className="h-4 w-4" />
            New Response
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4 text-red-600" />
          </button>
        </div>
      )}

      {/* Create/Edit Form */}
      {(isCreating || editingResponse) && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h4 className="font-medium text-gray-900 dark:text-white mb-4">
            {isCreating ? 'Create New Response' : 'Edit Response'}
          </h4>
          <ResponseForm
            initialData={
              editingResponse
                ? {
                    name: editingResponse.name,
                    shortcut: editingResponse.shortcut || '',
                    content: editingResponse.content,
                    category: editingResponse.category || 'General',
                    isPrivate: editingResponse.isPrivate,
                    isEmailTemplate: editingResponse.isEmailTemplate,
                    emailSubject: editingResponse.emailSubject || '',
                    htmlTemplateFileId: editingResponse.htmlTemplateFileId,
                    htmlTemplateFileName: editingResponse.htmlTemplateFileName,
                    useUploadedTemplate: editingResponse.useUploadedTemplate || false,
                  }
                : undefined
            }
            onSubmit={isCreating ? handleCreate : handleUpdate}
            onCancel={() => {
              setIsCreating(false);
              setEditingId(null);
            }}
            isEdit={!!editingResponse}
            isSaving={isSaving}
          />
        </div>
      )}

      {/* Search and Filter */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search responses..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Email Templates Toggle */}
          <label className="flex items-center gap-2 cursor-pointer px-3 py-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
            <input
              type="checkbox"
              checked={showEmailTemplatesOnly}
              onChange={(e) => setShowEmailTemplatesOnly(e.target.checked)}
              className="rounded text-purple-600"
            />
            <Mail className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            <span className="text-sm text-purple-700 dark:text-purple-300">Email Templates Only</span>
          </label>
        </div>

        {/* Category Filter */}
        <div className="flex items-center gap-2 flex-wrap mt-3">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
              selectedCategory === null
                ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            All
          </button>
          {DEFAULT_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                selectedCategory === cat.id
                  ? cat.color
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : filteredResponses.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
          <MessageSquare className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h4 className="font-medium text-gray-900 dark:text-white mb-1">No responses found</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {searchQuery || selectedCategory || showEmailTemplatesOnly
              ? 'Try adjusting your search or filter'
              : 'Create your first canned response to get started'}
          </p>
          {!searchQuery && !selectedCategory && (
            <button
              onClick={() => setIsCreating(true)}
              className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Create Response
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredResponses.map((response) => (
            <ResponseCard
              key={response.id}
              response={response}
              onEdit={() => setEditingId(response.id)}
              onDelete={() => setDeleteConfirm(response.id)}
              onDuplicate={() => handleDuplicate(response)}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Response</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this canned response? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Usage Tips */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Zap className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-200">Quick Tips</h4>
            <ul className="text-sm text-blue-700 dark:text-blue-300 mt-1 space-y-1">
              <li>• <strong>Email Templates:</strong> Enable "Use as Email Template" to use in Workflow Automation email actions</li>
              <li>• <strong>HTML Upload:</strong> Upload custom HTML templates for professional acknowledgement emails with your branding</li>
              <li>• <strong>Shortcuts:</strong> Use shortcuts like <code className="bg-blue-100 dark:bg-blue-800/30 px-1 rounded">/thanks</code> in the message composer</li>
              <li>• <strong>Variables:</strong> Add variables like <code className="bg-blue-100 dark:bg-blue-800/30 px-1 rounded">{`{{entity.contactName}}`}</code> for dynamic content</li>
              <li>• <strong>Workflow Automation:</strong> Go to Settings → Automations → Create rule with "Send Email" action</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
